import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { api } from '../../api.js';
import { useAuth } from '../../auth/AuthProvider.js';
import type { Account, AccountNode, Item } from '../../types/bookkeeping.js';

function flattenTree(nodes: AccountNode[]): Account[] {
  const result: Account[] = [];
  function walk(ns: AccountNode[]) {
    for (const n of ns) {
      result.push(n);
      if (n.children.length > 0) walk(n.children);
    }
  }
  walk(nodes);
  return result;
}

const ItemSchema = z.object({
  name: z.string().min(1).max(200),
  description: z.string().optional(),
  default_revenue_account_id: z.string().uuid().optional().or(z.literal('')),
  default_price_amount: z.string().optional(),
  default_price_currency: z.string().length(3).optional().or(z.literal('')),
});
type ItemForm = z.infer<typeof ItemSchema>;

function toFormDefaults(item?: Item): ItemForm {
  return {
    name: item?.name ?? '',
    description: item?.description ?? '',
    default_revenue_account_id: item?.default_revenue_account_id ?? '',
    default_price_amount: item?.default_price_amount ?? '',
    default_price_currency: item?.default_price_currency ?? '',
  };
}

function ItemModal({
  item,
  accounts,
  onClose,
  onSaved,
}: {
  item?: Item;
  accounts: Account[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation('bookkeeping');
  const isEdit = !!item;

  const [itemType, setItemType] = useState<'service' | 'inventory'>(item?.type ?? 'service');
  const [valuationMethod, setValuationMethod] = useState<'wac' | 'fifo'>(
    item?.valuation_method ?? 'wac',
  );
  const [defaultCost, setDefaultCost] = useState(item?.default_cost ?? '');
  const [defaultInventoryAcctId, setDefaultInventoryAcctId] = useState(
    item?.default_inventory_account_id ?? '',
  );
  const [defaultCogsAcctId, setDefaultCogsAcctId] = useState(item?.default_cogs_account_id ?? '');

  const assetAccounts = accounts.filter((a) => a.type === 'asset' && a.is_active);
  const expenseAccounts = accounts.filter((a) => a.type === 'expense' && a.is_active);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ItemForm>({
    resolver: zodResolver(ItemSchema),
    defaultValues: toFormDefaults(item),
  });

  const buildBody = (input: ItemForm) => ({
    name: input.name,
    description: input.description || null,
    default_revenue_account_id: input.default_revenue_account_id || null,
    default_price_amount: input.default_price_amount || null,
    default_price_currency: input.default_price_currency || null,
    type: itemType,
    valuation_method: itemType === 'inventory' ? valuationMethod : null,
    default_cost: defaultCost || null,
    default_inventory_account_id: defaultInventoryAcctId || null,
    default_cogs_account_id: defaultCogsAcctId || null,
  });

  const createMutation = useMutation({
    mutationFn: (input: ItemForm) =>
      api('/bookkeeping/items', {
        method: 'POST',
        body: JSON.stringify(buildBody(input)),
      }),
    onSuccess: onSaved,
  });

  const updateMutation = useMutation({
    mutationFn: (input: ItemForm) =>
      api(`/bookkeeping/items/${item?.id}`, {
        method: 'PATCH',
        body: JSON.stringify(buildBody(input)),
      }),
    onSuccess: onSaved,
  });

  const onSubmit = handleSubmit((data) =>
    isEdit ? updateMutation.mutate(data) : createMutation.mutate(data),
  );

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="w-full max-w-lg rounded-lg bg-white p-6 shadow-xl">
        <h2 className="mb-4 text-lg font-semibold">
          {isEdit ? t('items.editTitle') : t('items.addButton')}
        </h2>
        <form onSubmit={onSubmit} className="space-y-3">
          <label className="block">
            <span className="text-sm">{t('items.fields.name')} *</span>
            <input {...register('name')} className="mt-1 w-full rounded border px-3 py-2 text-sm" />
            {errors.name && <span className="text-xs text-red-600">{errors.name.message}</span>}
          </label>
          <label className="block">
            <span className="text-sm">{t('items.fields.description')}</span>
            <textarea
              {...register('description')}
              rows={2}
              className="mt-1 w-full rounded border px-3 py-2 text-sm"
            />
          </label>
          <label className="block">
            <span className="text-sm">{t('items.fields.revenueAccount')}</span>
            <select
              {...register('default_revenue_account_id')}
              className="mt-1 w-full rounded border px-3 py-2 text-sm"
            >
              <option value="">—</option>
              {accounts
                .filter((a) => a.type === 'revenue')
                .map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.code} — {a.name}
                  </option>
                ))}
            </select>
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-sm">{t('items.fields.defaultPrice')}</span>
              <input
                {...register('default_price_amount')}
                type="text"
                inputMode="numeric"
                placeholder="0"
                className="mt-1 w-full rounded border px-3 py-2 text-sm"
              />
            </label>
            <label className="block">
              <span className="text-sm">{t('items.fields.priceCurrency')}</span>
              <input
                {...register('default_price_currency')}
                maxLength={3}
                placeholder="USD"
                className="mt-1 w-full rounded border px-3 py-2 text-sm uppercase"
              />
            </label>
          </div>

          {/* Phase 4a — Item type selector */}
          <fieldset className="rounded border p-3">
            <legend className="px-1 text-sm font-medium">{t('items.type_section')}</legend>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="radio"
                  name="item_type"
                  value="service"
                  checked={itemType === 'service'}
                  onChange={() => setItemType('service')}
                />
                {t('items.type_service')}
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="radio"
                  name="item_type"
                  value="inventory"
                  checked={itemType === 'inventory'}
                  onChange={() => setItemType('inventory')}
                />
                {t('items.type_inventory')}
              </label>
            </div>
          </fieldset>

          {itemType === 'inventory' && (
            <fieldset className="rounded border p-3">
              <legend className="px-1 text-sm font-medium">{t('items.inventory_section')}</legend>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <span className="text-sm">{t('items.valuation_method')}</span>
                  <div className="mt-1 flex gap-3">
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="radio"
                        name="valuation"
                        value="wac"
                        checked={valuationMethod === 'wac'}
                        onChange={() => setValuationMethod('wac')}
                      />
                      WAC
                    </label>
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="radio"
                        name="valuation"
                        value="fifo"
                        checked={valuationMethod === 'fifo'}
                        onChange={() => setValuationMethod('fifo')}
                      />
                      FIFO
                    </label>
                  </div>
                </div>
                <div>
                  <label className="block">
                    <span className="text-sm">{t('items.default_cost')}</span>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={defaultCost}
                      onChange={(e) => setDefaultCost(e.target.value)}
                      placeholder="1000 = $10.00"
                      className="mt-1 w-full rounded border px-2 py-1 text-sm"
                    />
                  </label>
                </div>
                <div>
                  <label className="block">
                    <span className="text-sm">{t('items.default_inventory_account')}</span>
                    <select
                      value={defaultInventoryAcctId}
                      onChange={(e) => setDefaultInventoryAcctId(e.target.value)}
                      className="mt-1 w-full rounded border px-2 py-1 text-sm"
                    >
                      <option value="">— {t('items.use_default')} (1.1.03)</option>
                      {assetAccounts.map((a) => (
                        <option key={a.id} value={a.id}>
                          {a.code} — {a.name}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
                <div>
                  <label className="block">
                    <span className="text-sm">{t('items.default_cogs_account')}</span>
                    <select
                      value={defaultCogsAcctId}
                      onChange={(e) => setDefaultCogsAcctId(e.target.value)}
                      className="mt-1 w-full rounded border px-2 py-1 text-sm"
                    >
                      <option value="">— {t('items.use_default')} (5.1.01)</option>
                      {expenseAccounts.map((a) => (
                        <option key={a.id} value={a.id}>
                          {a.code} — {a.name}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
              </div>
            </fieldset>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded border px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || isPending}
              className="rounded bg-blue-600 px-4 py-2 text-sm text-white disabled:opacity-50"
            >
              {isEdit ? 'Guardar' : t('items.addButton')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export function Items() {
  const { t } = useTranslation('bookkeeping');
  const { activeOrgId } = useAuth();
  const qc = useQueryClient();
  const [addOpen, setAddOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<Item | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['bookkeeping-items', activeOrgId],
    queryFn: () => api<{ items: Item[] }>('/bookkeeping/items'),
    enabled: !!activeOrgId,
  });

  const { data: accountsData } = useQuery({
    queryKey: ['bookkeeping-accounts', activeOrgId],
    queryFn: () => api<{ tree: AccountNode[] }>('/bookkeeping/accounts'),
    enabled: !!activeOrgId,
  });

  const deactivate = useMutation({
    mutationFn: (id: string) => api(`/bookkeeping/items/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['bookkeeping-items'] }),
  });

  const handleSaved = () => {
    void qc.invalidateQueries({ queryKey: ['bookkeeping-items'] });
    setAddOpen(false);
    setEditTarget(null);
  };

  const items = data?.items ?? [];
  const accounts = flattenTree(accountsData?.tree ?? []);

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <h1 className="text-xl font-semibold">{t('items.title')}</h1>
        <button
          type="button"
          onClick={() => setAddOpen(true)}
          className="rounded bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700"
        >
          {t('items.addButton')}
        </button>
      </div>

      {isLoading ? (
        <p className="text-sm text-slate-500">{t('common:loading')}</p>
      ) : (
        <div className="overflow-hidden rounded border bg-white">
          <table className="w-full">
            <thead className="border-b text-left text-sm text-slate-600">
              <tr>
                <th className="p-3">{t('items.fields.name')}</th>
                <th className="p-3">{t('items.fields.description')}</th>
                <th className="p-3">{t('items.type_section')}</th>
                <th className="p-3">{t('items.fields.defaultPrice')}</th>
                <th className="p-3" />
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && (
                <tr>
                  <td colSpan={5} className="p-4 text-center text-sm text-slate-500">
                    No hay productos/servicios aún.
                  </td>
                </tr>
              )}
              {items.map((item) => (
                <tr key={item.id} className="border-b text-sm last:border-0">
                  <td className="p-3 font-medium">{item.name}</td>
                  <td className="p-3 text-slate-600">{item.description ?? '—'}</td>
                  <td className="p-3 text-slate-600">
                    {item.type === 'inventory'
                      ? t('items.type_inventory')
                      : t('items.type_service')}
                  </td>
                  <td className="p-3">
                    {item.default_price_amount
                      ? `${item.default_price_amount} ${item.default_price_currency ?? ''}`
                      : '—'}
                  </td>
                  <td className="p-3 text-right">
                    <button
                      type="button"
                      onClick={() => setEditTarget(item)}
                      className="mr-2 text-xs text-blue-600 hover:underline"
                    >
                      Editar
                    </button>
                    <button
                      type="button"
                      onClick={() => deactivate.mutate(item.id)}
                      className="text-xs text-red-600 hover:underline"
                    >
                      Desactivar
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {addOpen && (
        <ItemModal accounts={accounts} onClose={() => setAddOpen(false)} onSaved={handleSaved} />
      )}
      {editTarget && (
        <ItemModal
          item={editTarget}
          accounts={accounts}
          onClose={() => setEditTarget(null)}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
}
